import { _ } from "libs";
import React, { useState, useImperativeHandle, forwardRef } from "react";
import Modal from "./modal";
import withRedux from "../withRedux";
import { RootState } from "../reducers";
import { Card, SubscriptionPlan, SubscriptionPeriod } from "../types";
import PaymentWindow from "./paymentWindow";
import SubscriptionWindow from "./subscriptionWindow";
import { getPaymentCards, selectCard, selectPaymentNetwork } from "../actions";

type OnPayClicked = (card: Card, voucher?: string, promotionId?: string) => Promise<any>;

type Props = {
	onPayClicked?: OnPayClicked;
	amount?: number;
	currency?: string;
	description?: string;
	subscriptionMode?: boolean;
	cards: Card[];
	defaultCard: Card;
	context?: () => Promise<void>;
	precalculatedContext?: Promise<any>;
	getPaymentCards: () => Promise<void>;
	selectCard: (cardId: string) => void;
	selectPaymentNetwork: (network: string) => void;
	startTrial?: boolean;
	buttonLabel?: string;
};

const PaymentModal = (
	{
		onPayClicked,
		amount = 0,
		currency = "",
		description = "",
		subscriptionMode = false,
		cards,
		defaultCard,
		getPaymentCards,
		selectCard,
		selectPaymentNetwork,
		context,
		precalculatedContext,
		startTrial,
		buttonLabel,
	}: Props,
	ref
) => {
	const [step, setStep] = useState((subscriptionMode ? "subscription" : "payment") as "subscription" | "payment");
	const [isOpen, setOpen] = useState(false);
	const [paymentData, setPaymentData] = useState({ amount, currency, description });
	const [subscriptionPlan, setPlan] = useState(null as SubscriptionPlan);
	const [planId, setPlanId] = useState(null as string);

	useImperativeHandle(ref, () => ({
		open: () => {
			open();
		},
		close: () => {
			close();
		},
		setPlan: (plan: SubscriptionPlan) => {
			setPlan(plan);
		},
	}));

	const open = () => {
		setOpen(true);
	};

	const close = () => {
		setStep(subscriptionMode ? "subscription" : "payment");
		setPlan(null);
		setOpen(false);
	};

	const onSubscribe = (plan: SubscriptionPlan, period: string) => {
		const { id, amount, currency, metadata, nickname } = plan[period] as SubscriptionPeriod;
		const description = _("payment.subject.subscription", null, {
			period: _("payment.subject.period." + period),
			subscription: plan.product.name,
		});
		setPlanId(id);
		setPaymentData({ amount: amount / 100, currency, description });
		setStep("payment");
	};

	if (!isOpen) {
		return null;
	}

	return (
		<Modal title={step === "subscription" ? "" : _("payment.title")} onCloseClicked={close} className={step}>
			{step === "subscription" ? (
				<SubscriptionWindow subscriptionPlan={subscriptionPlan} onSubscribeClicked={onSubscribe} />
			) : null}
			{step === "payment" ? (
				<PaymentWindow
					{...paymentData}
					data={{ planId }}
					onPayClicked={onPayClicked}
					cards={cards}
					defaultCard={defaultCard}
					getPaymentCards={getPaymentCards}
					selectCard={selectCard}
					selectPaymentNetwork={selectPaymentNetwork}
					context={context}
					precalculatedContext={precalculatedContext}
					buttonLabel={buttonLabel ?? _("payBtn")}
					startTrial={startTrial}
				/>
			) : null}
		</Modal>
	);
};

export default withRedux(
	{ getPaymentCards, selectCard, selectPaymentNetwork },
	({ cards: { cards, defaultCard } }: RootState, ownProps) => {
		return { cards, defaultCard };
	},
	undefined,
	{ forwardRef: true }
)(forwardRef(PaymentModal));
